import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getResidentProfile, persistResidentAuthenticatedState, switchResidentProperty } from "@/api/residentAuth";
import { useLanguage } from "@/contexts/LanguageContext";

export default function ResidentDocumentAccess({ children }: { children: ReactNode }) {
  const [params] = useSearchParams();
  const targetBuilding = params.get("building");
  const { t } = useLanguage();
  const client = useQueryClient();
  const profile = useQuery({ queryKey: ["document-property-context", targetBuilding],
    queryFn: getResidentProfile, enabled: Boolean(targetBuilding) });
  const change = useMutation({ mutationFn: async (residentId: string) => {
    const response = await switchResidentProperty({ resident_id: residentId });
    if (!response.access_token) throw new Error("Property switch did not return a session");
    persistResidentAuthenticatedState(response);
    client.clear();
    // Reload all property-scoped layout state as well as document queries.
    // The current hash keeps the original document/building deep link.
    window.location.reload();
  } });

  if (!targetBuilding) return children;
  if (profile.isPending) return <p role="status">{t("common.loading")}</p>;
  if (profile.isError) return <div role="alert">
    <p>{t("m3.documents.propertyError")}</p>
    <button type="button" onClick={() => void profile.refetch()} className="mt-2 underline">{t("m3.retry")}</button>
  </div>;
  if (profile.data.data.building?.id === targetBuilding) return children;
  const membership = profile.data.data.memberships.find((item) => item.building.id === targetBuilding);
  if (!membership) return <p role="alert">{t("m3.documents.propertyUnavailable")}</p>;
  return <section className="space-y-4 rounded-2xl bg-white p-5 ring-1 ring-black/5">
    <p>{t("m3.documents.switchProperty", { building: membership.building.name || "" })}</p>
    <button type="button" disabled={change.isPending} onClick={() => change.mutate(membership.resident_id)}
      className="rounded-xl bg-[#1F3A93] px-4 py-2.5 text-white disabled:opacity-50">{t(change.isPending ? "common.loading" : "m3.documents.switchAndOpen")}</button>
    {change.isError && <p role="alert" className="text-red-700">{t("m3.documents.propertyError")}</p>}
  </section>;
}
